import { Component } from '@angular/core';
import { Wrapper } from '../../components/wrapper.component';
import { budgetAccountSelector } from '../../store/reselectors/accounts.reselector';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { AddLineItemsDialog } from './add-line-items.dialog.component';
import { JoiAccount, LineItemTemplate } from 'globals/store/accounts.data';
import { DropdownOption } from 'globals/store/programs.data';
import { jsonCopyObject, sortByProp, sortByPropOfNumber } from 'globals/helpers/helpers';
import { JoiInputComponent } from 'src/app/components/joi-input/joi-input.component';
import { DeleteButtonComponent } from 'src/app/components/buttons/delete-button.component';
import { ActionButtonComponent } from 'src/app/components/nav-bar/action-button.component';
import { CommonModule } from '@angular/common';
import { SortIconComponent } from 'src/app/components/nested-table/sort-icon.component';
import { PrettyNumbersPipe } from 'src/app/services/pipes/prettyNumbers.pipe';

@Component({
    selector: 'home-budget-line-items',
    template: `
<div class="line-items" *ngIf="!!account">
  <h5 class="header" (click)="sort('sortNumber')">
    <sort-icon *ngIf="sortKey === 'sortNumber'" [up]="sortUp"></sort-icon>
    SORT<br/>NUMBER
  </h5>
  <h5 class="header item" (click)="sort('name')">
    <sort-icon *ngIf="sortKey === 'name'" [up]="sortUp"></sort-icon>
    ITEM
  </h5>
  <h5 class="header" (click)="sort('unitRate')">
    <sort-icon *ngIf="sortKey === 'unitRate'" [up]="sortUp"></sort-icon>
    UNIT RATE
  </h5>
  <h5 class="header" (click)="sort('unitCost')">
    <sort-icon *ngIf="sortKey === 'unitCost'" [up]="sortUp"></sort-icon>
    UNIT COST
  </h5>
  <div></div>
  <ng-container *ngFor="let item of lineItems; trackBy: trackById">
    <joi-input 
      [(model)]="item.sortNumber" 
      [fixedWidth]="true"
      [placeholder]="'...'"
      [allowableCharacters]="'0123456789'"
      [disabled]="readOnly"
      (joiChange)="updateNumber(item, 'sortNumber', 1)"
    ></joi-input>
    <joi-input 
      [(model)]="item.name" 
      [placeholder]="'...'" 
      (joiChange)="save()"
      [disabled]="readOnly"
      [type]="'textarea'" 
      [autoSelect]="false" 
      [returnable]="true"
    ></joi-input>
    <joi-input 
      [(model)]="item.unitRate"
      [fixedWidth]="true"
      [placeholder]="'...'"
      [displayValue]="item.unitRate ? (item.unitRate | prettyNumbers) : null"
      [allowableCharacters]="'0123456789.'"
      [disabled]="readOnly"
      (joiChange)="updateNumber(item, 'unitRate')"
    ></joi-input>
    <joi-input 
      [(model)]="item.unitCost"
      [fixedWidth]="true"
      [placeholder]="'...'"
      [displayValue]="item.unitCost ? (item.unitCost | prettyNumbers) : null"
      [allowableCharacters]="'0123456789.'"
      [disabled]="readOnly"
      (joiChange)="updateNumber(item, 'unitCost')"
    ></joi-input>
    <delete-button *ngIf="!readOnly" [deleteTitle]="deleteTitle" [deleteText]="deleteText" (delete)="deleteItem(item.id)"></delete-button>
  </ng-container>
</div>

<button class="secondary" (click)="addLineItem()">
  + ADD NEW ITEM
</button>
<action-button class="import" [text]="'IMPORT'" (click)="importLineItems()"></action-button>
`,
    styleUrls: ['./home-budget-line-items.scss'],
    standalone: true,
    imports: [JoiInputComponent, PrettyNumbersPipe, SortIconComponent, DeleteButtonComponent, ActionButtonComponent, CommonModule]
})
export class HomeBudgetLineItemsComponent extends Wrapper<HomeBudgetLineItemsComponent> {
  _file = 'home-budget-line-items.component'
  account: JoiAccount
  lineItems: LineItemTemplate[] = []
  readOnly = false
  dropdownOptions: DropdownOption[] = [ {id: '1', title: 'Option one' }, { id: '2', title: 'Option two'}]
  test
  deleteTitle = 'Delete Line Item'
  deleteText = 'Are you sure you want to delete this line item?'
  sortKey: keyof LineItemTemplate = 'sortNumber'
  sortUp: boolean = false
  
  constructor() {
    super()
    this.selectCallback(budgetAccountSelector, null, (account: JoiAccount) => {
      this.account = account
      if (!account) {
        this.lineItems = []
        return
      }
      this.lineItems = jsonCopyObject(this.account.lineItems)
      this.sort(this.sortKey, true)
    })
  }

  async importLineItems() {
    console.log("Should be importing line items.")
    const lineItems: LineItemTemplate[] = await openDialogGetResult(this.dialog, AddLineItemsDialog, {
      width: '600px',
      autoFocus: false,
      backdropClass: 'clear',
    })
    if (!lineItems) return
    this.account.lineItems = jsonCopyObject([...this.account.lineItems, ...lineItems])
    this.accountService.saveLineItems(this.account)
  }

  sort(key: keyof LineItemTemplate, sortOnly = false) {
    if (this.sortKey !== key) {
      this.sortKey = key
      this.sortUp = true
      return
    }
    if (!sortOnly) this.sortUp = !this.sortUp
    switch (key) {
      case 'sortNumber':
      case 'unitCost':
      case 'unitRate':
        this.lineItems.sort(sortByPropOfNumber(key, this.sortUp ? -1 : 1))
        break
      case 'name':
        this.lineItems.sort(sortByProp(key, this.sortUp ? -1 : 1))
        break
      default:
        console.log("No sorting for " + key)
        break
    }
  }

  deleteItem(id: string) {
    this.lineItems = this.lineItems.filter(l => l.id !== id)
    this.save()
  }
    
  save() {
    const lineItems = [...this.lineItems]
    lineItems.sort(sortByPropOfNumber('sortNumber'))
    this.accountService.updateAccount(this.account.id, { lineItems }, { file: this._file, function: 'save' } )
  }

  addLineItem() {
    this.accountService.addLineItem(this.account)
  }

  updateNumber(item: LineItemTemplate, key: keyof LineItemTemplate, defaultValue = 0) {
    this.accountService.updateLineItemNumber(this.account, item, key, defaultValue)
  }

}

